import { Block, Floor, ApartmentUnit, Parking, Amenity } from '@/types/resources/property';
import dayjs from 'dayjs';

// Premium Apartment Society: Sunset Gardens
const WORKSPACE_ID = 'demo-workspace';

// Helper function to create workspace reference
const createWorkspaceRef = () => ({
  id: WORKSPACE_ID,
  name: 'Jardines del Atardecer',
  slug: 'demo-workspace'
});

// Helper function to create activity records
const createActivityRecord = (type: 'created' | 'updated' | 'deleted') => ({
  timestamp: dayjs().subtract(Math.floor(Math.random() * 30), 'days').toDate(),
  type,
  performedBy: {
    id: 'admin-001',
    displayName: 'Gerente de Propiedad'
  }
});

// Create 10 blocks with uniform, modern naming and proper floor structure
export const blocks: Block[] = [
  {
    id: 'BLK-A',
    name: 'Torre A',
    description: 'Ubicada en la esquina noreste de Jardines del Atardecer, con vista a la entrada principal y el horizonte de la ciudad',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-a-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre A`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-B',
    name: 'Torre B',
    description: 'Situada en la sección noroeste, adyacente al jardín central y área de juegos infantiles',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-b-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre B`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-C',
    name: 'Torre C',
    description: 'Torre central posicionada cerca del club principal y complejo de piscina',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-c-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre C`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-D',
    name: 'Torre D',
    description: 'Ubicada en la esquina sureste, frente a las canchas de tenis e instalaciones deportivas al aire libre',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-d-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre D`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-E',
    name: 'Torre E',
    description: 'Posicionada en el área suroeste, cerca del jardín y pista de caminata',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-e-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre E`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-F',
    name: 'Torre F',
    description: 'Torre central cerca de la fuente principal y salón de fiestas, ofreciendo vistas premium del complejo',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-f-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre F`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-G',
    name: 'Torre G',
    description: 'Ubicada en el ala este, adyacente a la biblioteca y sala de lectura',
    floors: Array.from({ length: 10 }, (_, i) => ({
      slug: `tower-g-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre G`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-H',
    name: 'Torre H',
    description: 'Situada en el ala oeste cerca del spa y centro de bienestar',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-h-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre H`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-I',
    name: 'Torre I',
    description: 'Posicionada en el área centro-norte, cerca del gimnasio y centro de fitness',
    floors: Array.from({ length: 20 }, (_, i) => ({
      slug: `tower-i-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre I`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'BLK-J',
    name: 'Torre J',
    description: 'Ubicada en el área centro-sur, cerca del área de BBQ y sala de juegos interior',
    floors: Array.from({ length: 8 }, (_, i) => ({
      slug: `tower-j-floor-${i + 1}`,
      name: `${i + 1}${getOrdinalSuffix(i + 1)} Piso`,
      description: `${i + 1}${getOrdinalSuffix(i + 1)} piso de la Torre J`
    })),
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Helper function for ordinal suffixes
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

// Create floors array for compatibility (flattened from all blocks)
export const floors: Floor[] = blocks.flatMap(block => block.floors || []);

// Create apartment units for each floor with proper numbering
export const units: ApartmentUnit[] = blocks.flatMap(block => {
  const blockUnits: ApartmentUnit[] = [];
  
  // Get floors for this block
  const blockFloors = block.floors || [];
  
  blockFloors.forEach(floor => {
    // Extract floor number from floor name (e.g., "1st Floor" -> 1)
    const floorNumber = parseInt(floor.name.split(' ')[0]);
    
    // Determine units per floor based on block (some variation for realism)
    let unitsPerFloor = 4; // Default
    if (block.id === 'BLK-A' || block.id === 'BLK-C' || block.id === 'BLK-F' || block.id === 'BLK-I') {
      unitsPerFloor = 6; // 20-floor towers get more units
    } else if (block.id === 'BLK-B' || block.id === 'BLK-E' || block.id === 'BLK-G') {
      unitsPerFloor = 5; // 10-floor towers get medium units
    } else {
      unitsPerFloor = 4; // 8-floor towers get fewer units
    }
    
    for (let unitNum = 1; unitNum <= unitsPerFloor; unitNum++) {
      const unitTypes = ['1BHK', '2BHK', '3BHK', '4BHK'];
      const unitType = unitTypes[Math.floor(Math.random() * unitTypes.length)];
      const area = unitType === '1BHK' ? 650 : unitType === '2BHK' ? 950 : unitType === '3BHK' ? 1250 : 1600;
      
      // Create proper unit number (e.g., 101, 102, 201, 202)
      const unitNumber = `${floorNumber}${unitNum.toString().padStart(2, '0')}`;
      
      blockUnits.push({
        id: `${block.id}-${unitNumber}`,
        number: unitNumber,
        bedrooms: parseInt(unitType[0]),
        bathrooms: Math.floor(Math.random() * 2) + 1,
        area: area,
        status: Math.random() > 0.2 ? 'occupied' : 'vacant',
        block: {
          id: block.id,
          name: block.name
        },
        floor: {
          name: floor.name,
          slug: floor.slug
        },
        description: `${unitType} apartment on ${floor.name} of ${block.name}`,
        workspace: createWorkspaceRef(),
        activityRecords: [createActivityRecord('created')]
      });
    }
  });
  
  return blockUnits;
});

// Create parking spots for all units
export const parkings: Parking[] = units.map((unit, index) => {
  const parkingTypes = ['car', 'bike', 'other'];
  const parkingType = parkingTypes[Math.floor(Math.random() * parkingTypes.length)] as 'car' | 'bike' | 'other';
  
  return {
    id: `PARK-${(index + 1).toString().padStart(3, '0')}`,
    number: `P${(index + 1).toString().padStart(3, '0')}`,
    type: parkingType,
    status: unit.status === 'occupied' ? 'occupied' : 'available',
    apartmentUnit: unit.status === 'occupied' ? {
      id: unit.id,
      number: unit.number
    } : undefined,
    block: unit.block,
    pricing: {
      amount: parkingType === 'car' ? 100 : parkingType === 'bike' ? 50 : 75,
      currency: 'USD',
      period: 'monthly'
    },
    description: `${parkingType} parking spot for ${unit.block?.name}`,
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  };
});

// Create comprehensive amenities for a premium apartment society
export const amenities: Amenity[] = [
  {
    id: 'AMEN-001',
    name: 'Piscina',
    type: 'swimming_pool',
    description: 'Piscina olímpica con control de temperatura',
    capacity: 50,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-C',
      name: 'Torre C'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-002',
    name: 'Gimnasio y Centro de Fitness',
    type: 'gym',
    description: 'Gimnasio completamente equipado con equipos modernos y entrenadores personales',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '05:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Torre A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-003',
    name: 'Club House',
    type: 'community_hall',
    description: 'Club house multiusos para eventos y reuniones',
    capacity: 200,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-B',
      name: 'Torre B'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-004',
    name: 'Cancha de Tenis',
    type: 'other',
    description: 'Cancha de tenis profesional con reflectores',
    capacity: 4,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-D',
      name: 'Torre D'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-005',
    name: 'Área de Juegos Infantiles',
    type: 'playground',
    description: 'Área de juegos segura y divertida para niños con equipos modernos',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '20:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Torre E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-006',
    name: 'Salón de Fiestas',
    type: 'community_hall',
    description: 'Elegante salón de fiestas para celebraciones y eventos',
    capacity: 150,
    status: 'active',
    operatingHours: {
      open: '10:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-F',
      name: 'Torre F'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-007',
    name: 'Biblioteca y Sala de Lectura',
    type: 'other',
    description: 'Biblioteca tranquila con extensa colección de libros y áreas de estudio',
    capacity: 25,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-G',
      name: 'Torre G'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-008',
    name: 'Spa y Centro de Bienestar',
    type: 'other',
    description: 'Spa relajante con terapia de masajes y tratamientos de bienestar',
    capacity: 10,
    status: 'active',
    operatingHours: {
      open: '09:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-H',
      name: 'Torre H'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-009',
    name: 'Área de BBQ',
    type: 'other',
    description: 'Área de BBQ al aire libre con asientos e instalaciones de cocina',
    capacity: 15,
    status: 'active',
    operatingHours: {
      open: '16:00',
      close: '23:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-I',
      name: 'Torre I'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-010',
    name: 'Sala de Juegos Interior',
    type: 'other',
    description: 'Sala de juegos con tenis de mesa, carrom y juegos de mesa',
    capacity: 20,
    status: 'active',
    operatingHours: {
      open: '08:00',
      close: '22:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-J',
      name: 'Torre J'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-011',
    name: 'Sala de Yoga y Meditación',
    type: 'other',
    description: 'Espacio tranquilo para yoga, meditación y clases de bienestar',
    capacity: 30,
    status: 'active',
    operatingHours: {
      open: '06:00',
      close: '21:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-A',
      name: 'Torre A'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  },
  {
    id: 'AMEN-012',
    name: 'Jardín y Pista de Caminata',
    type: 'garden',
    description: 'Hermoso jardín con pista de caminata y áreas de asiento',
    capacity: 100,
    status: 'active',
    operatingHours: {
      open: '00:00',
      close: '24:00',
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    },
    block: {
      id: 'BLK-E',
      name: 'Torre E'
    },
    workspace: createWorkspaceRef(),
    activityRecords: [createActivityRecord('created')]
  }
];

// Automatically generate block stats from the units, parkings, amenities arrays
export const mockBlockStats: { [blockId: string]: {
  totalApartmentUnits: number;
  totalParkings: number;
  totalAmenities: number;
  totalResidents: number;
} } = blocks.reduce((acc: { [blockId: string]: any }, block) => {
  const blockId = block.id;
  const blockUnits = units.filter(u => u.block?.id === blockId);
  const blockParkings = parkings.filter(p => p.block?.id === blockId);
  const blockAmenities = amenities.filter(a => a.block?.id === blockId);
  
  // Calculate residents based on occupied units (about 80% occupancy)
  const occupiedUnits = blockUnits.filter(unit => unit.status === 'occupied');
  const totalResidents = occupiedUnits.length;
  
  acc[blockId] = {
    totalApartmentUnits: blockUnits.length,
    totalParkings: blockParkings.length,
    totalAmenities: blockAmenities.length,
    totalResidents: totalResidents
  };
  return acc;
}, {});
